import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import type { DynamicDataExtraction } from '~/features/dynamic-data-extraction/types/DynamicDataExtraction';
import type { ExtractionAiField } from '~/features/dynamic-data-extraction/types/ExtractionAiField';
import type { ExtractionAiFieldCell } from '~/features/dynamic-data-extraction/types/ExtractionAiFieldCell';
import type { ExtractionDocument } from '~/features/dynamic-data-extraction/types/ExtractionDocument';
import type { PilotId } from '~/types/PilotId';

import { buildAiFieldCellKey } from '~/features/dynamic-data-extraction/utils/aiFieldCellKey';
import { loadDynamicDataExtractions } from '~/features/dynamic-data-extraction/utils/dynamicDataExtractionStorage';
import { getAiFieldCell } from '~/features/dynamic-data-extraction/utils/getAiFieldCell';
import { pruneOrphanedAiFieldCells } from '~/features/dynamic-data-extraction/utils/pruneOrphanedAiFieldCells';

import type { State } from './types';

const initialState: State = {
  items: loadDynamicDataExtractions(),
};

type CreateDynamicDataExtractionPayload = {
  createdAt?: string;
  createdBy: string;
  createdByUserId: PilotId;
  id?: string;
  title: string;
};

const dynamicDataExtractionSlice = createSlice({
  name: 'dynamicDataExtraction',
  initialState,
  selectors: {
    selectAllItems: (state: State) => Object.values(state.items),
    selectItemById: (state: State, extractionId: string) =>
      state.items[extractionId],
    selectAiFieldCell: (
      state: State,
      extractionId: string,
      documentHandlerId: string,
      fieldId: string,
    ) =>
      getAiFieldCell(
        state.items[extractionId]?.aiFieldCells,
        documentHandlerId,
        fieldId,
      ),
    selectAiFieldCellKey: (
      _state: State,
      documentHandlerId: string,
      fieldId: string,
    ) => buildAiFieldCellKey(documentHandlerId, fieldId),
  },
  reducers: {
    hydrateDynamicDataExtraction: (
      state,
      action: PayloadAction<DynamicDataExtraction>,
    ) => {
      state.items[action.payload.id] = action.payload;
    },
    createDynamicDataExtraction: {
      prepare: ({
        createdAt,
        createdBy,
        createdByUserId,
        id,
        title,
      }: CreateDynamicDataExtractionPayload) => ({
        payload: {
          id: id ?? uuid(),
          title,
          createdAt: createdAt ?? new Date().toISOString(),
          createdBy,
          createdByUserId,
          documents: [],
          aiFields: [],
        } satisfies DynamicDataExtraction,
      }),
      reducer: (state, action: PayloadAction<DynamicDataExtraction>) => {
        state.items[action.payload.id] = action.payload;
      },
    },
    updateDynamicDataExtractionTitle: (
      state,
      action: PayloadAction<{ extractionId: string; title: string }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (item) {
        item.title = action.payload.title;
      }
    },
    updateDynamicDataExtractionDocuments: (
      state,
      action: PayloadAction<{
        documents: ExtractionDocument[];
        extractionId: string;
      }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item) {
        return;
      }

      const previousHandlerIds = new Set(
        item.documents.map((document) => document.documentHandlerId),
      );
      const nextHandlerIds = new Set(
        action.payload.documents.map((document) => document.documentHandlerId),
      );
      const removedHandlerIds = new Set(item.removedDocumentHandlerIds ?? []);

      previousHandlerIds.forEach((handlerId) => {
        if (!nextHandlerIds.has(handlerId)) {
          removedHandlerIds.add(handlerId);
        }
      });
      nextHandlerIds.forEach((handlerId) => {
        removedHandlerIds.delete(handlerId);
      });

      item.documents = action.payload.documents;
      item.removedDocumentHandlerIds = [...removedHandlerIds];
    },
    addDraftAiFieldId: (
      state,
      action: PayloadAction<{ extractionId: string; fieldId: string }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item) {
        return;
      }

      const draftIds = new Set(item.draftAiFieldIds ?? []);
      draftIds.add(action.payload.fieldId);
      item.draftAiFieldIds = [...draftIds];
    },
    removeDraftAiFieldId: (
      state,
      action: PayloadAction<{ extractionId: string; fieldId: string }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item?.draftAiFieldIds?.length) {
        return;
      }

      item.draftAiFieldIds = item.draftAiFieldIds.filter(
        (fieldId) => fieldId !== action.payload.fieldId,
      );
    },
    updateDynamicDataExtractionAiFields: (
      state,
      action: PayloadAction<{
        aiFields: ExtractionAiField[];
        extractionId: string;
      }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item) {
        return;
      }

      item.aiFields = action.payload.aiFields;

      if (item.aiFieldCells) {
        item.aiFieldCells = pruneOrphanedAiFieldCells(
          item.aiFieldCells,
          action.payload.aiFields,
        );
      }
    },
    setAiFieldCell: (
      state,
      action: PayloadAction<{
        cell: ExtractionAiFieldCell;
        documentHandlerId: string;
        extractionId: string;
        fieldId: string;
      }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item) {
        return;
      }

      const fieldExists = item.aiFields.some(
        (field) => field.id === action.payload.fieldId,
      );

      if (!fieldExists) {
        return;
      }

      if (!item.aiFieldCells) {
        item.aiFieldCells = {};
      }

      item.aiFieldCells[
        buildAiFieldCellKey(
          action.payload.documentHandlerId,
          action.payload.fieldId,
        )
      ] = action.payload.cell;
    },
    markDdeModelDeleted: (
      state,
      action: PayloadAction<{ extractionId: string; modelId: string }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item) {
        return;
      }

      const deletedIds = new Set(item.deletedDdeModelIds ?? []);
      deletedIds.add(action.payload.modelId);
      item.deletedDdeModelIds = [...deletedIds];
    },
    unmarkDdeModelDeleted: (
      state,
      action: PayloadAction<{ extractionId: string; modelId: string }>,
    ) => {
      const item = state.items[action.payload.extractionId];

      if (!item?.deletedDdeModelIds?.length) {
        return;
      }

      item.deletedDdeModelIds = item.deletedDdeModelIds.filter(
        (id) => id !== action.payload.modelId,
      );
    },
    deleteDynamicDataExtraction: (
      state,
      action: PayloadAction<{ extractionId: string }>,
    ) => {
      delete state.items[action.payload.extractionId];
    },
  },
});

export default dynamicDataExtractionSlice;
